import { useState, useMemo, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAppContext } from "@/App";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, X } from "lucide-react";
import { BookingStepIndicator } from "@/components/booking/BookingStepIndicator";
import { MDRBadge } from "@/components/MDRBadge";
import {
  pharmaciesData, urgencyLevels, getEligibleRooms, getEligibleStructures,
  getAvailableSlots, getPharmacyById, getRoomById, getStructureById,
  serviceCatalogData, totalServicePrice, bookingsData
} from "@/data/platformData";
import { patientsData, serviceBundlesData, servicesData, getPatientById } from "@/data/mockData";

import { BookingPatientStep } from "./BookingPatientStep";
import { BookingServiceStep } from "./BookingServiceStep";
import { BookingUrgencyStep } from "./BookingUrgencyStep";
import { BookingSlotStep } from "./BookingSlotStep";
import { BookingConfirmStep } from "./BookingConfirmStep";

const STEPS = [
  { id: "patient", label: "Paziente" },
  { id: "service", label: "Prestazione" },
  { id: "urgency", label: "Urgenza" },
  { id: "slot", label: "Data & Sala" },
  { id: "confirm", label: "Conferma" }
];

export const BookingPage = () => {
  const navigate = useNavigate();
  const { activeRole, currentUser, selectedPatient: ctxPatient } = useAppContext();
  const [searchParams] = useSearchParams();

  // Context-aware initial state
  const initiator = activeRole?.id || searchParams.get("from") || "operator";
  const presetPharmacyId = searchParams.get("pharmacyId") || (initiator === "operator" ? "ph1" : null);
  const presetPatientId = searchParams.get("patientId");
  const presetService = searchParams.get("serviceId");

  const [currentStep, setCurrentStep] = useState(0);
  const [booking, setBooking] = useState({
    patient: presetPatientId ? getPatientById(presetPatientId) : (ctxPatient || null),
    pharmacyId: presetPharmacyId,
    serviceIds: presetService ? [presetService] : [],
    bundleId: null,
    urgency: "scheduled",
    date: null,
    time: null,
    diagnosticRoomId: null,
    structureId: null,
    notes: searchParams.get("notes") || ""
  });

  // For citizens, force pharmacy selection in service step (it's the first thing they pick)
  const isCitizen = initiator === "citizen";

  const eligibleRooms = useMemo(() => {
    if (!booking.pharmacyId || booking.serviceIds.length === 0) return [];
    return getEligibleRooms(booking.pharmacyId, booking.serviceIds);
  }, [booking.pharmacyId, booking.serviceIds]);

  const eligibleStructures = useMemo(() => {
    if (!booking.pharmacyId || booking.serviceIds.length === 0) return [];
    return getEligibleStructures(booking.pharmacyId, booking.serviceIds);
  }, [booking.pharmacyId, booking.serviceIds]);

  // Auto-pick first eligible structure & room when entering slot step
  useEffect(() => {
    if (currentStep === 3) {
      if (!booking.diagnosticRoomId && eligibleRooms[0]) {
        setBooking(b => ({ ...b, diagnosticRoomId: eligibleRooms[0].id }));
      }
      if (!booking.structureId && eligibleStructures[0]) {
        setBooking(b => ({ ...b, structureId: eligibleStructures[0].id }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, eligibleRooms, eligibleStructures]);

  const updateBooking = (patch) => setBooking(b => ({ ...b, ...patch }));

  const canGoNext = () => {
    switch (currentStep) {
      case 0: return !!booking.patient;
      case 1: return booking.serviceIds.length > 0 && !!booking.pharmacyId;
      case 2: return !!booking.urgency;
      case 3: return !!booking.date && !!booking.time && !!booking.diagnosticRoomId;
      default: return true;
    }
  };

  const handleNext = () => {
    if (!canGoNext()) {
      toast.error("Completa lo step corrente prima di procedere");
      return;
    }
    if (currentStep === STEPS.length - 1) {
      handleConfirm();
    } else {
      setCurrentStep(s => s + 1);
    }
  };

  const handleBack = () => {
    if (currentStep === 0) {
      navigate(-1);
    } else {
      setCurrentStep(s => s - 1);
    }
  };

  const handleConfirm = () => {
    const newBooking = {
      id: `bk${bookingsData.length + 1}_${Date.now()}`,
      patientId: booking.patient.id,
      pharmacyId: booking.pharmacyId,
      diagnosticRoomId: booking.diagnosticRoomId,
      serviceIds: booking.serviceIds,
      bundleId: booking.bundleId,
      urgency: booking.urgency,
      date: booking.date,
      time: booking.time,
      duration: booking.serviceIds.reduce((acc, sid) => {
        const s = servicesData.find(x => x.id === sid);
        return acc + (s?.duration || 5);
      }, 0),
      status: "confirmed",
      structureId: booking.structureId,
      bookedBy: currentUser?.id || initiator,
      bookedByRole: initiator,
      createdAt: new Date().toISOString(),
      notes: booking.notes
    };

    bookingsData.unshift(newBooking);

    const pharmacy = getPharmacyById(booking.pharmacyId);
    const structure = booking.structureId ? getStructureById(booking.structureId) : null;

    toast.success("Prenotazione confermata", {
      description: `Reminder inviato a ${booking.patient.name}${structure ? ` · Placeholder a ${structure.name}` : ""}`
    });

    // Navigate based on initiator
    if (initiator === "citizen") {
      navigate("/patient");
    } else if (initiator === "specialist") {
      navigate("/doctor");
    } else if (initiator === "pharmacy_manager") {
      navigate("/manager");
    } else {
      navigate("/dashboard");
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <BookingPatientStep
            booking={booking}
            updateBooking={updateBooking}
            initiator={initiator}
          />
        );
      case 1:
        return (
          <BookingServiceStep
            booking={booking}
            updateBooking={updateBooking}
            initiator={initiator}
            isCitizen={isCitizen}
          />
        );
      case 2:
        return <BookingUrgencyStep booking={booking} updateBooking={updateBooking} />;
      case 3:
        return (
          <BookingSlotStep
            booking={booking}
            updateBooking={updateBooking}
            eligibleRooms={eligibleRooms}
            eligibleStructures={eligibleStructures}
          />
        );
      case 4:
        return <BookingConfirmStep booking={booking} initiator={initiator} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-background pb-32 md:pb-12">
      {/* Header */}
      <header className="sticky top-0 z-30 border-b bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/80">
        <div className="max-w-5xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(-1)}
              data-testid="booking-close-btn"
            >
              <X className="h-5 w-5" />
            </Button>
            <div className="min-w-0">
              <h1 className="text-base sm:text-lg font-semibold leading-tight truncate">
                Nuova Prenotazione
              </h1>
              <p className="text-xs text-muted-foreground truncate">
                {initiator === "citizen" ? "Portale Cittadino" :
                 initiator === "specialist" ? "Console Specialista" :
                 initiator === "pharmacy_manager" ? "Responsabile Farmacia" :
                 "Operatore di Farmacia"}
              </p>
            </div>
          </div>
          <MDRBadge variant="admin" size="sm" className="hidden sm:inline-flex" />
        </div>
      </header>

      {/* Stepper */}
      <div className="max-w-5xl mx-auto px-4 md:px-8 pt-6">
        <BookingStepIndicator steps={STEPS} currentStep={currentStep} />
      </div>

      {/* Step content */}
      <main className="max-w-5xl mx-auto px-4 md:px-8 pt-8">
        <Card className="p-5 md:p-8 border-2 animate-fade-in-up">
          {renderStep()}
        </Card>
      </main>

      {/* Sticky bottom action bar */}
      <div className="fixed bottom-0 inset-x-0 z-30 border-t bg-card/95 backdrop-blur md:relative md:border-0 md:mt-8 md:bg-transparent">
        <div className="max-w-5xl mx-auto px-4 md:px-8 py-3 md:py-4 flex items-center justify-between gap-3">
          <Button
            variant="outline"
            onClick={handleBack}
            className="gap-2"
            data-testid="booking-back-btn"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="hidden sm:inline">{currentStep === 0 ? "Annulla" : "Indietro"}</span>
          </Button>

          <div className="text-xs text-muted-foreground text-center hidden md:block">
            {STEPS[currentStep]?.label} · Step {currentStep + 1} di {STEPS.length}
          </div>

          <Button
            onClick={handleNext}
            disabled={!canGoNext()}
            variant="premium"
            className="gap-2"
            data-testid="booking-next-btn"
          >
            {currentStep === STEPS.length - 1 ? "Conferma Prenotazione" : "Avanti"}
            <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BookingPage;
